/**
 * Utilitários para validação de documentos brasileiros (CPF/CNPJ)
 */

/**
 * Valida CPF brasileiro
 * @param {string} cpf - CPF para validar (pode conter caracteres especiais)
 * @returns {boolean} - true se o CPF for válido
 */
export const validateCPF = (cpf) => {
	const cleanCPF = cpf.replace(/\D/g, '');

	if (cleanCPF.length !== 11 || /^(\d)\1{10}$/.test(cleanCPF)) return false;

	let sum = 0;
	for (let i = 0; i < 9; i++) {
		sum += parseInt(cleanCPF[i]) * (10 - i);
	}
	let check1 = 11 - (sum % 11);
	if (check1 >= 10) check1 = 0;

	if (parseInt(cleanCPF[9]) !== check1) return false;

	sum = 0;
	for (let i = 0; i < 10; i++) {
		sum += parseInt(cleanCPF[i]) * (11 - i);
	}
	let check2 = 11 - (sum % 11);
	if (check2 >= 10) check2 = 0;

	return parseInt(cleanCPF[10]) === check2;
};

/**
 * Valida CNPJ brasileiro
 * @param {string} cnpj - CNPJ para validar (pode conter caracteres especiais)
 * @returns {boolean} - true se o CNPJ for válido
 */
export const validateCNPJ = (cnpj) => {
	// CNPJ aceita alfanumérico (RFB 2026+). Os 12 primeiros podem ser A-Z ou 0-9;
	// os 2 últimos (DV) continuam sendo dígitos. Cada caractere vale (charCode - 48).
	const cleanCNPJ = cnpj.replace(/[^A-Za-z0-9]/g, '').toUpperCase();

	if (cleanCNPJ.length !== 14) return false;
	if (!/^[A-Z0-9]{12}[0-9]{2}$/.test(cleanCNPJ)) return false;
	if (/^(.)\1{13}$/.test(cleanCNPJ)) return false;

	const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
	const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

	const value = (i) => cleanCNPJ.charCodeAt(i) - 48;

	let sum = 0;
	for (let i = 0; i < 12; i++) {
		sum += value(i) * weights1[i];
	}
	let check1 = sum % 11;
	check1 = check1 < 2 ? 0 : 11 - check1;

	if (value(12) !== check1) return false;

	sum = 0;
	for (let i = 0; i < 13; i++) {
		sum += value(i) * weights2[i];
	}
	let check2 = sum % 11;
	check2 = check2 < 2 ? 0 : 11 - check2;

	return value(13) === check2;
};

/**
 * Valida documento brasileiro (CPF ou CNPJ)
 * @param {Object} params - Parâmetros de validação
 * @param {string} params.type - Tipo do documento ('CPF' ou 'CNPJ')
 * @param {string} params.number - Número do documento
 * @returns {boolean} - true se o documento for válido
 */
export const validateDocument = ({ type, number }) => {
	switch (type) {
		case 'CPF':
			return validateCPF(number.replace(/\D/g, ''));
		case 'CNPJ':
			return validateCNPJ(number.replace(/[^A-Za-z0-9]/g, '').toUpperCase());
		default:
			return number.replace(/\D/g, '').length > 0;
	}
};

/**
 * Formata CPF com máscaras
 * @param {string} cpf - CPF sem formatação
 * @returns {string} - CPF formatado (000.000.000-00)
 */
export const formatCPF = (cpf) => {
	const cleanCPF = cpf.replace(/\D/g, '');
	if (cleanCPF.length !== 11) return cpf;
	return cleanCPF.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

/**
 * Formata CNPJ com máscaras
 * @param {string} cnpj - CNPJ sem formatação
 * @returns {string} - CNPJ formatado (00.000.000/0000-00)
 */
export const formatCNPJ = (cnpj) => {
	// Suporta CNPJ alfanumérico: mantém A-Z e 0-9; DV (últimos 2) ainda é numérico.
	const cleanCNPJ = cnpj.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
	if (cleanCNPJ.length !== 14) return cnpj;
	return cleanCNPJ.replace(/(.{2})(.{3})(.{3})(.{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

/**
 * Auto-detecta o tipo de documento baseado no comprimento
 * @param {string} number - Número do documento (apenas dígitos)
 * @returns {string|null} - 'CPF', 'CNPJ' ou null se não conseguir detectar
 */
export const detectDocumentType = (number) => {
	// CPF é estritamente numérico (11 dígitos).
	// CNPJ pode ser alfanumérico (14 caracteres A-Z/0-9, DV numérico).
	const alnum = number.replace(/[^A-Za-z0-9]/g, '');
	if (alnum.length === 11 && /^\d{11}$/.test(alnum)) return 'CPF';
	if (alnum.length === 14) return 'CNPJ';
	return null;
};